import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Timer, ArrowRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DISMISS_KEY = 'boost_ribbon_dismissed_at';

const fmtRemaining = (ms) => {
    if (ms <= 0) return 'ending';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h left`;
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
};

/**
 * Sits above the lobby welcome banner whenever an admin-started referral bonus
 * boost is active. Self-fetches `/user/referral` (cached 30s via interval) so
 * existing players notice the limited-time payout the moment they land.
 */
const BoostRibbon = () => {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [dismissed, setDismissed] = useState(false);

    const fetchInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/user/referral`, { withCredentials: true });
            setInfo(data);
        } catch { /* silent — referrals tab still works */ }
    }, []);

    useEffect(() => {
        fetchInfo();
        const id = setInterval(fetchInfo, 30_000);
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => { clearInterval(id); clearInterval(tick); };
    }, [fetchInfo]);

    // Hide if the player dismissed this *exact* boost window (keyed by expiry).
    useEffect(() => {
        if (!info?.boost_expires_at) { setDismissed(false); return; }
        try {
            const stored = localStorage.getItem(DISMISS_KEY);
            setDismissed(stored === info.boost_expires_at);
        } catch { setDismissed(false); }
    }, [info?.boost_expires_at]);

    const handleDismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, info.boost_expires_at); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (!info?.boost_active || dismissed) return null;
    const expiresMs = new Date(info.boost_expires_at).getTime();
    const remaining = expiresMs - now;
    if (remaining <= 0) return null;

    return (
        <AnimatePresence>
            <motion.button
                type="button"
                onClick={() => navigate('/referrals')}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="group w-full text-left relative overflow-hidden rounded-2xl border border-[#FF6B00]/40 bg-gradient-to-r from-[#FF6B00]/15 via-[#FFD700]/10 to-[#FF6B00]/15 p-4 sm:p-5 hover:from-[#FF6B00]/25 hover:to-[#FF6B00]/25 transition-colors"
                data-testid="lobby-boost-ribbon"
            >
                {/* Animated flame glow strip */}
                <div className="pointer-events-none absolute inset-0 opacity-30" style={{
                    background: 'radial-gradient(circle at 0% 50%, rgba(255,107,0,0.4), transparent 40%)',
                }} />

                <div className="relative flex items-center gap-3 sm:gap-4 flex-wrap">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FF6B00]/20 flex items-center justify-center">
                        <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6B00] animate-pulse" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="px-2 py-0.5 rounded-full bg-[#FF6B00] text-white text-[10px] font-black uppercase tracking-wider">
                                Boost Live
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-[#FFD700] font-mono tabular-nums">
                                <Timer className="w-3.5 h-3.5" />
                                {fmtRemaining(remaining)}
                            </span>
                        </div>
                        <p className="text-white font-bold mt-1 text-base sm:text-lg leading-tight">
                            Earn <span className="text-[#39FF14]">${info.referrer_bonus}</span> Free Play per friend who loads ${info.load_threshold ?? 20}
                            {Number(info.referee_bonus || 0) > 0 && (
                                <>
                                    <span className="text-[#A0AAB2] font-normal"> · they get </span>
                                    <span className="text-[#39FF14]">${info.referee_bonus}</span>
                                </>
                            )}
                        </p>
                        {info.base_referrer_bonus < info.referrer_bonus && (
                            <p className="text-xs text-[#A0AAB2] mt-0.5">
                                Normally <span className="line-through">${info.base_referrer_bonus}</span> — limited time only.
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <span className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-[#FFD700] group-hover:translate-x-1 transition-transform">
                            Share my link <ArrowRight className="w-4 h-4" />
                        </span>
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDismiss(); } }}
                            aria-label="Dismiss boost banner"
                            className="p-1.5 rounded-full text-[#A0AAB2] hover:bg-white/10 hover:text-white"
                            data-testid="lobby-boost-dismiss"
                        >
                            <X className="w-4 h-4" />
                        </span>
                    </div>
                </div>
            </motion.button>
        </AnimatePresence>
    );
};

export default BoostRibbon;
